import React, { useState } from 'react'

import {
  Box,
  Card,
  Form,
  Text,
  Title,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import {
  RichTextDisplay,
  RichTextEditor,
  RichTextEditorHandle,
} from '@factorialco/f0-react/dist/experimental'
import {
  CompetencyFragment,
  CreateLearningPlatformEditModeDocument,
  EditTrainingTrainingFragment,
  UpdateLearningPlatformEditModeDocument,
} from 'generated/resources/graphql'

import Selector from 'modules/core/components/Selector'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DirectUploader from 'modules/trainings/components/Files/Uploaders/DirectUploader'
import { sumMinutesToHours } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/useHoursAndMinutes'
import NumberField from 'modules/trainings/components/NumberField'
import { CourseValidity } from 'modules/trainings/components/Revamp/CourseValidity'
import CourseValidityUnCheckModal from 'modules/trainings/components/Revamp/TrainingEditMode/CourseValidityUnCheckModal'
import {
  BasicInfoFormValues,
  LearningPlatformFormValues,
} from 'modules/trainings/components/Revamp/TrainingEditMode/types'
import { useEditTrainingContext } from 'modules/trainings/hooks/EditTraining/useEditTrainingContext'
import { useUpdateTrainingBasicInfo } from 'modules/trainings/hooks/Trainings/useUpdateTrainingBasicInfo'
import { useValidation } from 'modules/trainings/hooks/useValidation'
import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'

export const BasicInfo = ({
  competencies,
  saved,
  training,
}: {
  competencies: CompetencyFragment[]
  saved: boolean
  training: EditTrainingTrainingFragment
}) => {
  const objectivesRichTextEditorRef = React.useRef<RichTextEditorHandle>(null)
  const objectivesTextEditorRef = React.useRef<EditorHandle>(null)
  const descriptionRichTextEditorRef = React.useRef<RichTextEditorHandle>(null)
  const descriptionTextEditorRef = React.useRef<EditorHandle>(null)
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const { company } = useCurrentSession()
  const learningPlatform = training.learningPlatform
  const { setIsDirty, setIsDisabled, setSaved, courseValidityToggle, setCourseValidityToggle } =
    useEditTrainingContext()
  const [openModal, setOpenModal] = useState(false)
  const [enableLP, setEnableLP] = useState<boolean>(!!learningPlatform)
  const { updateBasicInfoTraining, loading: loadingBI } = useUpdateTrainingBasicInfo({
    onCompleted(data) {
      if (data.trainingsMutations.updateBasicInfoTrainingsTraining?.errors.length) {
        return
      }
    },
  })

  const [createLearningPlatform, { loading: loadingCreateLP }] = useMutation(
    CreateLearningPlatformEditModeDocument,
    {
      extractorFn: (data) => data.trainingsMutations.createTrainingsLearningPlatform,
      update: (cache, { data }) => {
        const newLP = data?.trainingsMutations.createTrainingsLearningPlatform.learningPlatform

        if (!newLP) return

        cache.modify({
          id: `TrainingsTraining:${training.id}`,
          fields: {
            learningPlatform() {
              return newLP
            },
          },
        })

        cache.gc()
      },
    }
  )

  const [updateLearningPlatform, { loading: loadingUpdateLP }] = useMutation(
    UpdateLearningPlatformEditModeDocument,
    {
      extractorFn: (data) => data.trainingsMutations.updateTrainingsLearningPlatform,
    }
  )

  const { validateLink } = useValidation()
  const readonly = !training._policies?.edit.can
  const {
    values: lpValues,
    dirty: lpDirty,
    handleSubmit: lpHandleSubmit,
    resetForm: resetLPFrom,
    isSubmitting: lpIsSubmitting,
    errors: lpErrors,
    setValues: lpSetValues,
  } = useForm<LearningPlatformFormValues>({
    defaultValues: {
      hasLearningPlatform: !!learningPlatform,
      name: learningPlatform?.name ?? '',
      link: learningPlatform?.link ?? '',
      startDate: learningPlatform?.startDate ?? undefined,
      endDate: learningPlatform?.endDate ?? undefined,
      duration: learningPlatform?.duration ?? undefined,
    },
    validate: (data) => {
      const errors: FormErrors<LearningPlatformFormValues> = {}
      const errorLink = validateLink(data.link)
      if (data.hasLearningPlatform) {
        if (!data.name) {
          errors.name = i18n.t('common.empty')
        }
        if (!data.link) {
          errors.link = i18n.t('trainings.common.link_validation')
        }
        if (errorLink) {
          errors.link = errorLink
        }
        return errors
      }
    },
    onSubmit: async (data) => {
      if (learningPlatform) {
        const lpResult = await updateLearningPlatform({
          variables: {
            link: data.link,
            name: data.name,
            startDate: data.startDate,
            endDate: data.endDate,
            duration: data.duration,
            id: learningPlatform.id,
          },
        })

        const handled = handleResult(lpResult, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.trainings_detail.training_details_error'),
          })
        })
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('trainings.trainings_detail.training_details_updated'),
          })
          const values = lpValues
          resetLPFrom({ values })
        }

        return handled
      } else {
        if (data.hasLearningPlatform) {
          const lpResult = await createLearningPlatform({
            variables: {
              link: data.link,
              name: data.name,
              startDate: data.startDate,
              endDate: data.endDate,
              duration: data.duration,
              companyId: company.id,
              trainingId: training.id,
            },
          })
          const handled = handleResult(lpResult, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('trainings.trainings_detail.training_details_not_updated'),
            })
          })
          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('trainings.trainings_detail.training_details_updated'),
            })
            const values = lpValues
            resetLPFrom({ values })
          }

          return handled
        } else {
          return Promise.resolve({ success: true, data: '' })
        }
      }
    },
  })
  const initialThumbnail =
    training.thumbnail && training.filename
      ? new AttachedFile({ url: training?.thumbnail, filename: training.filename })
      : undefined
  const form = useForm<BasicInfoFormValues>({
    defaultValues: {
      name: String(training?.name),
      description: training?.description || '',
      objectives: training?.objectives ?? '',
      competencyIds: training?.competencyIds || [],
      validFor: training?.validFor ?? undefined,
      totalDuration: training?.totalDuration ?? 0,
      thumbnail: undefined,
      thumbnailDelete: false,
      isMandatory: training?.isMandatory ?? false,
    },
    validate: (data) => {
      const errors: FormErrors<BasicInfoFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('common.empty')
        setIsDisabled(true)
      }

      return errors
    },
    onSubmit: async (data) => {
      const result = await updateBasicInfoTraining({
        ...data,
        id: training.id,
        validFor: courseValidityToggle ? data.validFor : undefined,
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.trainings_detail.training_details_not_updated'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.trainings_detail.training_details_updated'),
        })

        const values = form.values
        form.resetForm({ values })
      }

      return handled
    },
  })

  const onSubmit = async () => {
    await form.handleSubmit()
    await lpHandleSubmit()
    setSaved(false)
  }

  useEffect(() => {
    if (
      saved &&
      (!form.isSubmitting || !loadingBI || !loadingCreateLP || !loadingUpdateLP || !lpIsSubmitting)
    ) {
      onSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, form.isSubmitting, loadingBI, loadingCreateLP, loadingUpdateLP])

  useEffect(() => {
    setIsDirty(form.dirty || lpDirty || !!training.validFor !== courseValidityToggle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.dirty, lpDirty, training.validFor, courseValidityToggle])

  useEffect(() => {
    if (form.errors.name || lpErrors.name || lpErrors.link) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.errors.name, lpErrors.name, lpErrors.link])

  useEffect(() => {
    lpSetValues({ ...lpValues, hasLearningPlatform: enableLP })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableLP])

  useEffect(() => {
    setCourseValidityToggle(!!training.validFor)
  }, [training?.validFor])

  const setOpenModalAction = (value: boolean) => {
    setCourseValidityToggle(value)
    setOpenModal(!value)
  }
  const [hours, setHours] = useState((): number => {
    const duration_hours = form.values.totalDuration
      ? parseFloat(String(form.values.totalDuration))
      : 0
    return Math.floor(duration_hours)
  })
  const [minutes, setMinutes] = useState((): number => {
    const duration_minutes = form.values.totalDuration
      ? parseFloat(String(form.values.totalDuration))
      : 0
    return Math.round((duration_minutes % 1) * 60)
  })

  const handleDurationChange = (newHours: number, newMinutes: number) => {
    const durationInHours = sumMinutesToHours(newHours, newMinutes)
    form.setValues({ ...form.values, totalDuration: durationInHours })
    setHours(newHours)
    setMinutes(newMinutes)
  }

  return (
    <Box padding="s24" gap="s24">
      <Title
        title={i18n.t('trainings.edit_mode.basic_information.title')}
        description={i18n.t('trainings.edit_mode.basic_information.new_description')}
      />
      <Form onSubmit={() => null} padding={null}>
        <TextField
          mandatory
          readonly={readonly}
          value={form.values.name || undefined}
          label={i18n.t('trainings.edit_mode.basic_information.training_name_title')}
          description={i18n.t('trainings.edit_mode.basic_information.training_name_description')}
          placeholder={i18n.t('trainings.edit_mode.basic_information.training_name_placeholder')}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.errors.name}
        />

        <Box paddingBottom="s8" gap="s4">
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('trainings.edit_mode.basic_information.thumbnail_title')}
          </Text.Normal>
          <Text.Normal size="200" weight="normal" color="grey800">
            {i18n.t('trainings.edit_mode.basic_information.thumbnail_description')}
          </Text.Normal>
        </Box>
        <DirectUploader
          onUpload={async (uploadData) => {
            form.setValues({
              ...form.values,
              thumbnail: uploadData.file,
              thumbnailDelete: false,
            })
          }}
          onDelete={() => {
            form.setValues({ ...form.values, thumbnail: undefined, thumbnailDelete: true })
          }}
          description={i18n.t('trainings.edit_mode.basic_information.thumbnail_placeholder')}
          fileTypes={['image' as const]}
          resourceType={'Trainings::Training'}
          resourceId={String(training.id)}
          initialFile={initialThumbnail ? initialThumbnail.toFileObject('image/jpeg') : undefined}
        />
        <Box paddingBottom="s8" gap="s4">
          <Text.Normal size="200" color="grey1000" weight="medium">
            {i18n.t('trainings.edit_mode.basic_information.objectives.title')}
          </Text.Normal>
          <Text.Normal size="200" weight="normal" color="grey800">
            {i18n.t('trainings.edit_mode.basic_information.objectives.description')}
          </Text.Normal>
          {richTextEditorLoading ? (
            <RichTextEditor.Skeleton />
          ) : canUseRichTextEditor && !readonly ? (
            readonly ? (
              <RichTextDisplay content={form.values.objectives || ''} />
            ) : (
              <RichTextEditor
                ref={objectivesRichTextEditorRef}
                {...editorConfig}
                onChange={(data) =>
                  form.setValues({ ...form.values, objectives: data?.value || '' })
                }
                placeholder=""
                initialEditorState={{ content: form.values.objectives || '' }}
                title={i18n.t('trainings.edit_mode.basic_information.objectives.title')}
              />
            )
          ) : (
            <TextEditor
              ref={objectivesTextEditorRef}
              readonly={readonly}
              placeholder=""
              height="s200"
              initialEditorState={{
                data: form.values.objectives,
              }}
              onChange={(value) =>
                form.setValues({ ...form.values, objectives: String(value ?? '') })
              }
            />
          )}
        </Box>
        <Text.Normal size="200" color="grey1000" weight="medium">
          {i18n.t('trainings.edit_mode.basic_information.description_title')}
        </Text.Normal>
        {richTextEditorLoading ? (
          <RichTextEditor.Skeleton />
        ) : canUseRichTextEditor && !readonly ? (
          <RichTextEditor
            ref={descriptionRichTextEditorRef}
            {...editorConfig}
            onChange={(data) => form.setValues({ ...form.values, description: data?.value || '' })}
            placeholder=""
            initialEditorState={{ content: form.values.description || '' }}
            title={i18n.t('trainings.edit_mode.basic_information.description_title')}
          />
        ) : (
          <TextEditor
            ref={descriptionTextEditorRef}
            readonly={readonly}
            placeholder=""
            height="s200"
            initialEditorState={{
              data: form.values.description,
            }}
            onChange={(value) =>
              form.setValues({ ...form.values, description: String(value ?? '') })
            }
          />
        )}
        <Selector.Popover
          label={i18n.t('trainings.trainings_detail.competencies')}
          description={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.basic_information.competencies_description'
          )}
          isMultiSelect
          blankLabel={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.basic_information.competencies_blank'
          )}
          placeholder={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.basic_information.competencies_placeholder'
          )}
          value={form.values.competencyIds || []}
          onChange={(ids) => {
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            form.setValues({ ...form.values, competencyIds: (ids as string[]) || [] })
          }}
          inputStyle="tags"
        >
          {competencies.map(({ id, name }) => (
            <Selector.Option key={id} value={id} label={name} searchValue={name}>
              <Box flexDirection="row" gap="s8" alignItems="center">
                <Text.Normal>{name}</Text.Normal>
              </Box>
            </Selector.Option>
          ))}
        </Selector.Popover>

        <Box gap="s4" flexDirection="column">
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.total_course_duration'
            )}
          </Text.Normal>
          <Box flexDirection="row" gap="s16">
            <Box gap="s4" flexDirection="column">
              <Text.Normal size="200" weight="normal" color="grey800">
                {i18n.t('trainings.trainings_revamp.new_training.wizard.basic_information.hours')}
              </Text.Normal>
              <NumberField
                step={1}
                min={0}
                value={hours}
                onChange={(value) => {
                  handleDurationChange(value ?? 0, minutes)
                }}
              />
            </Box>
            <Box gap="s4" flexDirection="column">
              <Text.Normal size="200" weight="normal" color="grey800">
                {i18n.t('trainings.trainings_revamp.new_training.wizard.basic_information.minutes')}
              </Text.Normal>
              <NumberField
                step={1}
                min={0}
                max={59}
                maxDecimals={0}
                value={minutes}
                onChange={(value) => {
                  handleDurationChange(hours, value ?? 0)
                }}
              />
            </Box>
          </Box>
        </Box>

        <Card>
          <Card.Title
            size="small"
            action={
              <TogglerCheckbox
                value={form.values.isMandatory ?? false}
                onChange={(value) => form.setValues({ ...form.values, isMandatory: value })}
              />
            }
          >
            {i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.mandatory_training.title'
            )}
          </Card.Title>
          <Card.Description>
            {i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.mandatory_training.description'
            )}
          </Card.Description>
        </Card>
        <CourseValidity
          open={courseValidityToggle}
          data={form.values}
          setOpen={setOpenModalAction}
          onChange={({ validFor }) => form.setValues({ ...form.values, validFor: validFor })}
        />
      </Form>
      {openModal && <CourseValidityUnCheckModal setOpen={setOpenModal} />}
    </Box>
  )
}
